// src/components/Visualization/KpiCard.js
import React from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import TrendLine from './TrendLine';

/**
 * KpiCard - Componente avançado para exibição de métricas com tendências e contexto
 * 
 * @param {Object} props
 * @param {string} props.title - Título do KPI
 * @param {string|number} props.value - Valor principal
 * @param {string} props.prefix - Prefixo do valor (ex: R$, $)
 * @param {string} props.suffix - Sufixo do valor (ex: %, pts)
 * @param {number} props.change - Mudança percentual (ex: 12.3 para +12.3%)
 * @param {string} props.timeframe - Período de comparação (ex: "vs. mês anterior")
 * @param {Array} props.trendData - Dados para mini gráfico de tendência
 * @param {string} props.trendKey - Chave para acessar os dados no array de tendência
 * @param {string} props.color - Cor base (accent color - usará variações para gráfico)
 * @param {string} props.className - Classes adicionais para estilização
 * @param {boolean} props.autoColor - Definir cores automaticamente com base na mudança
 * @param {boolean} props.inverseTrend - Inverte a lógica de positivo/negativo (ex: para CPA, menor é melhor)
 */
const KpiCard = ({
  title,
  value,
  prefix = '',
  suffix = '',
  change,
  timeframe = 'vs. anterior',
  trendData = [],
  trendKey,
  color = 'blue',
  className = '',
  autoColor = true,
  inverseTrend = false,
  children
}) => {
  // Determina se a mudança é positiva ou negativa
  const isPositive = inverseTrend ? change < 0 : change > 0;
  const isNeutral = change === 0 || isNaN(change);
  
  // Retorna as classes de cores baseadas na direção da mudança
  const getTrendColor = () => {
    if (autoColor) {
      if (isNeutral) return 'text-gray-500';
      return isPositive ? 'text-green-600' : 'text-red-600';
    }
    
    // Se autoColor é false, usa a cor definida pelo usuário
    return `text-${color}-600`;
  };
  
  // Background para o ícone de tendência
  const getTrendBgColor = () => {
    if (autoColor) {
      if (isNeutral) return 'bg-gray-100';
      return isPositive ? 'bg-green-100' : 'bg-red-100';
    }
    
    return `bg-${color}-100`;
  };
  
  // Formata valor para exibição com prefix, valor e suffix
  const formattedValue = () => {
    // Se o valor for número, formata com pontos para milhares
    if (!isNaN(value)) {
      const num = parseFloat(value);
      // Se o valor for muito grande (mais de 1 milhão), simplifica para K ou M
      if (num >= 1000000) {
        return `${prefix}${(num / 1000000).toFixed(1)}M${suffix}`;
      } else if (num >= 1000) {
        return `${prefix}${(num / 1000).toFixed(1)}K${suffix}`;
      } else if (Number.isInteger(num)) {
        return `${prefix}${num.toLocaleString('pt-BR')}${suffix}`;
      } else {
        return `${prefix}${num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${suffix}`;
      }
    }
    
    // Se não for número, apenas retorna com prefix e suffix
    return `${prefix}${value}${suffix}`;
  };
  
  // Componente de ícone de tendência
  const TrendIcon = () => {
    if (isNeutral) return <Minus className="h-4 w-4" />;
    return isPositive ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />;
  };
  
  return (
    <div className={`stat-card overflow-hidden ${className}`}>
      <div className="p-5">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm text-gray-500 font-medium">{title}</h3>
          {change !== undefined && (
            <div className={`flex items-center ${getTrendColor()} text-xs font-semibold`}>
              <span className={`flex items-center justify-center rounded-full p-1 mr-1 ${getTrendBgColor()}`}>
                <TrendIcon />
              </span>
              <span>{isNeutral ? 'Estável' : `${change > 0 ? '+' : ''}${Math.abs(change).toFixed(1)}%`}</span>
            </div>
          )}
        </div>
        
        <div className="flex items-end justify-between">
          <div className="text-2xl font-bold text-gray-800">
            {formattedValue()}
          </div>
          {timeframe && <div className="text-xs text-gray-500">{timeframe}</div>}
        </div>
        
        {/* Mini gráfico de tendência */}
        {trendData && trendData.length > 0 && trendKey && (
          <div className="mt-4 h-12">
            <TrendLine 
              data={trendData} 
              dataKey={trendKey} 
              color={autoColor ? (isPositive ? 'green' : 'red') : color}
              showTooltip
              lineWidth={2}
            />
          </div>
        )}
        
        {children && <div className="mt-4">{children}</div>}
      </div>
    </div>
  );
};

export default KpiCard;
